import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, AlertCircle, ChevronDown } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../ui/DropdownMenu';
import { apiService } from '../../services/api';
import { useToast } from '../ui/Toaster';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import styled from 'styled-components';

interface LateFeePolicy {
  id?: string;
  propertyId?: string;
  unitId?: string;
  tenantId?: string;
  gracePeriod: number;
  lateFeeType: 'PERCENTAGE' | 'FIXED_AMOUNT' | 'DAILY_RATE';
  lateFeeAmount: number;
  lateFeeDays?: number | null;
  maxLateFees?: number;
}

interface LateFeePolicyManagerProps {
  propertyId?: string;
  onPolicyChange?: (policy: LateFeePolicy | null) => void;
}

const PolicyContainer = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: ${props => props.theme.muted}20;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
`;

const PolicyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const PolicyTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0;
`;

const PolicyForm = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormFieldFull = styled(FormField)`
  grid-column: 1 / -1;
`;

const ErrorMessage = styled.div`
  font-size: 12px;
  color: ${props => props.theme.destructive};
  margin-top: 4px;
`;

const InfoMessage = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DropdownTrigger = styled.button<{ $error: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  background: ${props => props.theme.input};
  border: 1px solid ${props => props.$error ? props.theme.destructive : props.theme.border};
  border-radius: 6px;
  color: ${props => props.theme.foreground};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.ring};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.ring};
    box-shadow: 0 0 0 2px ${props => props.theme.ring}20;
  }
`;

const PercentageInputContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
`;

const PercentageInputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
`;

const CalculatedAmountDisplay = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: ${props => props.theme.muted}40;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 14px;
  color: ${props => props.theme.mutedForeground};
  min-height: 40px;
`;

const SampleRentNote = styled.div`
  font-size: 11px;
  color: ${props => props.theme.mutedForeground};
  margin-top: 4px;
  font-style: italic;
`;

const DropdownValue = styled.span<{ $placeholder: boolean }>`
  color: ${props => props.$placeholder ? props.theme.mutedForeground : props.theme.foreground};
`;

const PolicyActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: ${props => props.theme.mutedForeground};
  font-size: 14px;
`;

const LateFeePolicyManager: React.FC<LateFeePolicyManagerProps> = ({
  propertyId,
  onPolicyChange
}) => {
  const toast = useToast();
  const [policy, setPolicy] = useState<LateFeePolicy | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const defaultPolicy: LateFeePolicy = {
    propertyId,
    gracePeriod: 5,
    lateFeeType: 'PERCENTAGE',
    lateFeeAmount: 5.0,
    maxLateFees: undefined
  };

  const [formData, setFormData] = useState<LateFeePolicy>(defaultPolicy);

  // Load existing policy when propertyId is available
  useEffect(() => {
    if (propertyId) {
      loadPolicy();
    }
  }, [propertyId]);

  const loadPolicy = async () => {
    if (!propertyId) return;
    
    try {
      setIsLoading(true);
      const response = await apiService.request('GET', `/late-fee-policies?propertyId=${propertyId}`);
      const loaded = response.data?.policy;
      if (loaded && loaded.propertyId === propertyId) {
        setPolicy(loaded);
        setFormData(loaded);
        onPolicyChange?.(loaded);
      } else {
        setPolicy(null);
        setFormData({ ...defaultPolicy, propertyId });
      }
    } catch (error: any) {
      // Policy not found is OK - it means we'll create a new one
      if (error.response?.status !== 404) {
        console.error('Error loading late fee policy:', error);
      }
      setPolicy(null);
      setFormData({ ...defaultPolicy, propertyId });
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.gracePeriod < 0 || formData.gracePeriod > 30) {
      newErrors.gracePeriod = 'Grace period must be between 0 and 30 days';
    }

    if (formData.lateFeeAmount < 0) {
      newErrors.lateFeeAmount = 'Late fee amount must be non-negative';
    }

    if (formData.lateFeeType === 'PERCENTAGE' && formData.lateFeeAmount > 100) {
      newErrors.lateFeeAmount = 'Percentage cannot exceed 100%';
    }

    if (formData.lateFeeType === 'DAILY_RATE') {
      if (!formData.lateFeeDays || formData.lateFeeDays < 1 || formData.lateFeeDays > 365) {
        newErrors.lateFeeDays = 'Enter chargeable days between 1 and 365';
      }
    }

    if (formData.maxLateFees !== undefined && formData.maxLateFees < 0) {
      newErrors.maxLateFees = 'Max late fees must be non-negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      const policyData = {
        ...formData,
        propertyId,
        lateFeeDays: formData.lateFeeType === 'DAILY_RATE' ? formData.lateFeeDays : null,
      };

      const isPropertyScopedPolicy = policy?.id && policy.propertyId === propertyId;

      if (isPropertyScopedPolicy) {
        const response = await apiService.request('PUT', `/late-fee-policies/${policy.id}`, policyData);
        const saved = response.data?.policy ?? { ...policy, ...policyData };
        setPolicy(saved);
        setFormData(saved);
        onPolicyChange?.(saved);
      } else {
        const response = await apiService.request('POST', '/late-fee-policies', policyData);
        const saved = response.data?.policy;
        if (saved) {
          setPolicy(saved);
          setFormData(saved);
          onPolicyChange?.(saved);
        }
      }

      toast.success('Late fee policy saved');
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error saving late fee policy:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to save policy' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!policy?.id) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!policy?.id) return;

    try {
      setIsLoading(true);
      await apiService.request('DELETE', `/late-fee-policies/${policy.id}`);
      setPolicy(null);
      setFormData(defaultPolicy);
      onPolicyChange?.(null);
      setIsEditing(false);
      toast.success('Late fee policy deleted', {
        description: 'The late fee policy has been successfully removed.',
      });
    } catch (error: any) {
      console.error('Error deleting late fee policy:', error);
      toast.error('Failed to delete policy', {
        description: error.response?.data?.error || 'An error occurred while deleting the policy.',
      });
      setErrors({ submit: error.response?.data?.error || 'Failed to delete policy' });
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCancel = () => {
    if (policy) {
      setFormData(policy);
    } else {
      setFormData(defaultPolicy);
    }
    setIsEditing(false);
    setErrors({});
  };

  const handleInputChange = (field: keyof LateFeePolicy, value: any) => {
    if (field === 'lateFeeType') {
      setFormData((prev) => ({
        ...prev,
        lateFeeType: value,
        lateFeeDays: value === 'DAILY_RATE' ? (prev.lateFeeDays ?? 7) : undefined,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    if (field === 'lateFeeType' && errors.lateFeeDays) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.lateFeeDays;
        return newErrors;
      });
    }
  };

  const formatPolicyAmount = (policyData: LateFeePolicy) => {
    if (policyData.lateFeeType === 'PERCENTAGE') {
      return `${policyData.lateFeeAmount}% of rent`;
    }
    if (policyData.lateFeeType === 'DAILY_RATE') {
      const days = policyData.lateFeeDays ?? 0;
      return `KES ${policyData.lateFeeAmount.toLocaleString()} / day × ${days} day${days === 1 ? '' : 's'}`;
    }
    return `KES ${policyData.lateFeeAmount.toLocaleString()} flat fee`;
  };

  const lateFeeTypeOptions = [
    { value: 'PERCENTAGE', label: 'Percentage of Rent' },
    { value: 'FIXED_AMOUNT', label: 'Fixed Amount' },
    { value: 'DAILY_RATE', label: 'Daily Rate' }
  ];

  if (isLoading && !policy) {
    return <EmptyState>Loading policy...</EmptyState>;
  }

  return (
    <PolicyContainer>
      <PolicyHeader>
        <PolicyTitle>Late Fee Policy</PolicyTitle>
        {!isEditing && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {policy ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit size={14} />
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                >
                  <Trash2 size={14} />
                  Delete
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                <Plus size={14} />
                Add Policy
              </Button>
            )}
          </div>
        )}
      </PolicyHeader>

      {isEditing ? (
        <div>
          <PolicyForm>
            <FormField>
              <Label htmlFor="gracePeriod">Grace Period (Days) *</Label>
              <Input
                id="gracePeriod"
                type="number"
                value={formData.gracePeriod.toString()}
                onChange={(e) => handleInputChange('gracePeriod', parseInt(e.target.value) || 0)}
                min="0"
                max="30"
                error={!!errors.gracePeriod}
              />
              {errors.gracePeriod && <ErrorMessage>{errors.gracePeriod}</ErrorMessage>}
              <InfoMessage>
                <AlertCircle size={12} />
                Days after due date before late fee applies
              </InfoMessage>
            </FormField>

            <FormField>
              <Label htmlFor="lateFeeType">Late Fee Type *</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <DropdownTrigger $error={!!errors.lateFeeType}>
                    <DropdownValue $placeholder={!formData.lateFeeType}>
                      {formData.lateFeeType 
                        ? lateFeeTypeOptions.find(opt => opt.value === formData.lateFeeType)?.label
                        : 'Select type'}
                    </DropdownValue>
                    <ChevronDown size={16} style={{ opacity: 0.6 }} />
                  </DropdownTrigger>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {lateFeeTypeOptions.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => handleInputChange('lateFeeType', option.value)}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {errors.lateFeeType && <ErrorMessage>{errors.lateFeeType}</ErrorMessage>}
            </FormField>

            <FormFieldFull>
              <Label htmlFor="lateFeeAmount">
                {formData.lateFeeType === 'PERCENTAGE' 
                  ? 'Late Fee Amount (%) *'
                  : formData.lateFeeType === 'DAILY_RATE'
                  ? 'Daily Rate (KES) *'
                  : 'Fixed Amount (KES) *'}
              </Label>
              {formData.lateFeeType === 'PERCENTAGE' ? (
                <>
                  <PercentageInputContainer>
                    <PercentageInputWrapper>
                      <Input
                        id="lateFeeAmount"
                        type="number"
                        step="0.01"
                        value={formData.lateFeeAmount.toString()}
                        onChange={(e) => handleInputChange('lateFeeAmount', parseFloat(e.target.value) || 0)}
                        min="0"
                        max="100"
                        error={!!errors.lateFeeAmount}
                        style={{ paddingRight: '32px' }}
                      />
                      <span style={{ 
                        position: 'absolute', 
                        right: '12px', 
                        color: 'var(--muted-foreground)',
                        fontSize: '14px',
                        pointerEvents: 'none'
                      }}>%</span>
                    </PercentageInputWrapper>
                    <CalculatedAmountDisplay>
                      {formData.lateFeeAmount > 0 ? (
                        <span>
                          ≈ KES {((10000 * formData.lateFeeAmount) / 100).toLocaleString('en-KE', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })} <span style={{ fontSize: '11px', opacity: 0.7 }}>(based on KES 10,000 rent)</span>
                        </span>
                      ) : (
                        <span style={{ opacity: 0.5 }}>Calculated amount will appear here</span>
                      )}
                    </CalculatedAmountDisplay>
                  </PercentageInputContainer>
                  <SampleRentNote>
                    * Calculation shown is based on a sample rent of KES 10,000. Actual late fee will be calculated based on the tenant's rent amount.
                  </SampleRentNote>
                </>
              ) : (
                <Input
                  id="lateFeeAmount"
                  type="number"
                  step="0.01"
                  value={formData.lateFeeAmount.toString()}
                  onChange={(e) => handleInputChange('lateFeeAmount', parseFloat(e.target.value) || 0)}
                  min="0"
                  error={!!errors.lateFeeAmount}
                />
              )}
              {errors.lateFeeAmount && <ErrorMessage>{errors.lateFeeAmount}</ErrorMessage>}
            </FormFieldFull>

            {formData.lateFeeType === 'DAILY_RATE' && (
              <FormField>
                <Label htmlFor="lateFeeDays">Chargeable Days *</Label>
                <Input
                  id="lateFeeDays"
                  type="number"
                  value={formData.lateFeeDays?.toString() ?? ''}
                  onChange={(e) =>
                    handleInputChange('lateFeeDays', e.target.value ? parseInt(e.target.value, 10) : undefined)
                  }
                  min="1"
                  max="365"
                  error={!!errors.lateFeeDays}
                />
                {errors.lateFeeDays && <ErrorMessage>{errors.lateFeeDays}</ErrorMessage>}
                <InfoMessage>
                  <AlertCircle size={12} />
                  Number of days the daily rate runs, starting after the grace period ends
                </InfoMessage>
                {formData.lateFeeAmount > 0 && formData.lateFeeDays && formData.lateFeeDays > 0 && (
                  <SampleRentNote>
                    Max late fee at full duration: KES{' '}
                    {(formData.lateFeeAmount * formData.lateFeeDays).toLocaleString('en-KE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </SampleRentNote>
                )}
              </FormField>
            )}

            <FormField>
              <Label htmlFor="maxLateFees">
                {formData.lateFeeType === 'DAILY_RATE' ? 'Max Late Fees (KES)' : 'Max Late Fees (KES)'}
              </Label>
              <Input
                id="maxLateFees"
                type="number"
                step="0.01"
                value={formData.maxLateFees?.toString() || ''}
                onChange={(e) => handleInputChange('maxLateFees', e.target.value ? parseFloat(e.target.value) : undefined)}
                min="0"
                placeholder="No limit"
                error={!!errors.maxLateFees}
              />
              {errors.maxLateFees && <ErrorMessage>{errors.maxLateFees}</ErrorMessage>}
              <InfoMessage>
                <AlertCircle size={12} />
                {formData.lateFeeType === 'DAILY_RATE'
                  ? 'Optional: cap the total late fee in KES'
                  : 'Optional: maximum late fee cap'}
              </InfoMessage>
            </FormField>


            {errors.submit && (
              <FormFieldFull>
                <ErrorMessage>{errors.submit}</ErrorMessage>
              </FormFieldFull>
            )}

            <FormFieldFull>
            <PolicyActions>
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="md"
                onClick={handleSave}
                loading={isLoading}
                disabled={isLoading}
              >
                {policy ? 'Update Policy' : 'Create Policy'}
              </Button>
            </PolicyActions>
            </FormFieldFull>
          </PolicyForm>
        </div>
      ) : policy ? (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
            <div>
              <strong>Grace Period:</strong> {policy.gracePeriod} days
            </div>
            <div>
              <strong>Type:</strong> {lateFeeTypeOptions.find(opt => opt.value === policy.lateFeeType)?.label}
            </div>
            <div>
              <strong>Amount:</strong> {formatPolicyAmount(policy)}
            </div>
            {policy.lateFeeType === 'DAILY_RATE' && policy.lateFeeDays && (
              <div>
                <strong>Chargeable Days:</strong> {policy.lateFeeDays} day{policy.lateFeeDays === 1 ? '' : 's'} after grace
              </div>
            )}
            {policy.maxLateFees && (
              <div>
                <strong>Max Late Fees:</strong> KES {policy.maxLateFees.toLocaleString()}
              </div>
            )}
          </div>
        </div>
      ) : (
        <EmptyState>
          No late fee policy configured. Click "Add Policy" to create one.
        </EmptyState>
      )}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Late Fee Policy"
        description="Are you sure you want to delete this late fee policy? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        isLoading={isLoading}
      />
    </PolicyContainer>
  );
};

export default LateFeePolicyManager;

